import React, { useState, useRef, useEffect } from "react";

interface EditableProps {
  text: string;
  onEdit: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}

const EditableDiv: React.FC<EditableProps> = ({
  text,
  onEdit,
  className = "",
  style = {},
  placeholder = "Click to edit",
}) => {
  const [value, setValue] = useState(text);
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(text);
  }, [text]);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const newValue = e.currentTarget.innerText;
    setValue(newValue);
    onEdit(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      divRef.current?.blur();
    }
  };

  return (
    <div
      ref={divRef}
      contentEditable
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`cursor-text outline-none ${className}`}
      style={{
        ...style,
        transition: "all 0.2s ease",
      }}
      suppressContentEditableWarning
    >
      {value || placeholder}
    </div>
  );
};

interface SlideContent {
  title?: string;
  subtitle?: string;
  topic?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const HeaderTitleSlide2 = {
  id: "header-title2-slide",
  name: "Header Title Slide (Glass Card)",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
    },
    {
      id: "topic",
      label: "Topic",
      type: "text",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
    },
  ],
  render: (content: SlideContent) => {
    const {
      title = "",
      subtitle = "",
      topic = "",
      bgColor,
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      // Update the configuration in PreviewSlides
      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Animated Background Blobs */}
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-white/20 rounded-full mix-blend-overlay filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute -bottom-8 right-4 w-96 h-96 bg-white/20 rounded-full mix-blend-overlay filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-white/20 rounded-full mix-blend-overlay filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        {/* Glass Card Container */}
        <div className="relative z-10 flex items-center justify-center h-full p-12">
          <div className="backdrop-blur-lg bg-white/10 rounded-3xl p-12 w-full max-w-5xl shadow-glass">
            {/* Topic Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full backdrop-blur-lg bg-white/10 mb-12">
              <div className="w-2 h-2 rounded-full bg-white mr-3"></div>
              <EditableDiv
                text={topic}
                onEdit={handleEdit("topic")}
                className="text-sm font-medium"
                placeholder="Enter topic..."
              />
            </div>

            {/* Title Container */}
            <div className="space-y-8">
              <EditableDiv
                text={title}
                onEdit={handleEdit("title")}
                className="text-7xl font-black tracking-tight leading-none animate-slide-up"
                placeholder="Enter title..."
              />

              {/* Animated Separator */}
              <div className="flex items-center space-x-4">
                <div className="h-px flex-grow bg-gradient-to-r from-white/50 to-transparent animate-width"></div>
                <div className="w-2 h-2 rounded-full bg-white/50 animate-pulse"></div>
              </div>

              {/* Subtitle */}
              <EditableDiv
                text={subtitle}
                onEdit={handleEdit("subtitle")}
                className="text-3xl font-light text-white/90 max-w-3xl animate-slide-up animation-delay-300"
                placeholder="Enter subtitle..."
              />
            </div>

            {/* Decorative Elements */}
            <div className="absolute bottom-8 right-8 flex items-center space-x-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="w-12 h-1 rounded-full bg-white/20 animate-pulse"
                  style={{ animationDelay: `${i * 200}ms` }}
                ></div>
              ))}
            </div>
          </div>
        </div>

        {/* Corner Decorations */}
        <div className="absolute top-8 right-8 flex flex-col items-end space-y-2">
          <div className="w-24 h-px bg-white/20"></div>
          <div className="w-16 h-px bg-white/15"></div>
          <div className="w-8 h-px bg-white/10"></div>
        </div>
      </div>
    );
  },
};

// Add required styles to the global CSS
const style = document.createElement("style");
style.textContent = `
  @keyframes blob {
    0% {
      transform: translate(0px, 0px) scale(1);
    }
    33% {
      transform: translate(30px, -50px) scale(1.1);
    }
    66% {
      transform: translate(-20px, 20px) scale(0.9);
    }
    100% {
      transform: translate(0px, 0px) scale(1);
    }
  }

  @keyframes slide-up {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes width {
    from { width: 0; }
    to { width: 100%; }
  }

  .animate-blob {
    animation: blob 7s infinite;
  }

  .animate-slide-up {
    animation: slide-up 0.8s ease-out forwards;
  }

  .animate-width {
    animation: width 1s ease-out forwards;
  }

  .animation-delay-2000 {
    animation-delay: 2s;
  }

  .animation-delay-4000 {
    animation-delay: 4s;
  }

  .animation-delay-300 {
    animation-delay: 300ms;
  }

  .shadow-glass {
    box-shadow: 0 0 40px rgba(0, 0, 0, 0.1),
                0 0 80px rgba(255, 255, 255, 0.1),
                inset 0 0 4px rgba(255, 255, 255, 0.2);
  }
`;
document.head.appendChild(style);

// Declare global window interface
declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default HeaderTitleSlide2;
